import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useApiQuery, useApiMutation } from '../../hooks/useApiQuery';
import {
  getEventUsers,
  addEventUser,
  updateEventUser,
  deleteEventUser,
  getAllEvents,
  copyUsersToEvent,
  lookupAttendeeForEvent,
} from '../../services/api';
 import { useSocket } from '../../context/SocketContext';
import { Html5Qrcode } from 'html5-qrcode';

import {
  PlusIcon,
  ClipboardIcon,
  PencilIcon,
  TrashIcon,
  QrCodeIcon,
  XMarkIcon,
  UserGroupIcon,
  CameraIcon,
} from '@heroicons/react/24/outline';

import { QRCodeCanvas } from 'qrcode.react';

// import { useSocket } from '../../context/SocketContext';

const Users = () => {
  const { user: currentUser } = useAuth();
  const socket = useSocket();

  const { eventId } = useParams();

  // =====================================================
  // MODALS
  // =====================================================

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const [showCopyModal, setShowCopyModal] = useState(false);

  // =====================================================
  // QR SCANNER
  // =====================================================

  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState('');

  const scannerRef = useRef(null);

  // =====================================================
  // COPY EVENT
  // =====================================================

  const [selectedSourceEventId, setSelectedSourceEventId] =
    useState('');

  // =====================================================
  // FORM
  // =====================================================

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    status: 'active',
    customId: '',
    userId: '',
  });

  const [customData, setCustomData] = useState({});

  // =====================================================
  // GET EVENT USERS
  // =====================================================

  const {
    data: users = [],
    isLoading: usersLoading,
    refetch: refetchUsers,
  } = useApiQuery(
    ['event-users', eventId],
    () =>
      getEventUsers(eventId).then(
        res => res.data
      ),
    {
      enabled: !!eventId,
      staleTime: 5 * 60 * 1000,

      onError: () => {
        alert(
          'Failed to fetch users. Check console.'
        );
      },
    }
  );

  // =====================================================
  // CUSTOM FIELDS
  // =====================================================

  const {
    data: customFields = [],
    refetch: refetchCustomFields,
  } = useApiQuery(
    ['custom-fields'],
    () =>
      api
        .get('/custom-fields')
        .then(res => res.data),

    {
      staleTime: 5 * 60 * 1000,

      onError: () => {
        console.error(
          'Failed to load custom fields'
        );
      },
    }
  );

  // =====================================================
  // ALL EVENTS
  // =====================================================

  const {
    data: allEvents = [],
  } = useApiQuery(
    ['all-events'],

    async () => {
      const res = await getAllEvents();

      return Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];
    },

    {
      staleTime: 5 * 60 * 1000,

      onError: () => {
        console.error(
          'Failed to load events'
        );
      },
    }
  );

  const copyableEvents = useMemo(() => {
    return allEvents.filter(
      event => event._id !== eventId
    );
  }, [allEvents, eventId]);

  // =====================================================
  // ADD EVENT USER
  // =====================================================

  const addUserMutation = useApiMutation(
    data =>
      addEventUser(
        eventId,
        data
      ).then(res => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
        handleCloseModal();
      },

      onError: error => {
        console.error(
          'Error saving user:',
          error
        );

        alert(
          'Error saving user: ' +
            (
              error.response?.data?.message ||
              'Unknown error'
            )
        );
      },
    }
  );

  // =====================================================
  // UPDATE EVENT USER
  // =====================================================

  const updateUserMutation = useApiMutation(
    ({ userId, data }) =>
      updateEventUser(
        eventId,
        userId,
        data
      ).then(res => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
        handleCloseModal();
      },

      onError: error => {
        alert(
          'Error saving user: ' +
            (
              error.response?.data?.message ||
              'Unknown error'
            )
        );
      },
    }
  );

  // =====================================================
  // DELETE EVENT USER
  // =====================================================

  const deleteUserMutation = useApiMutation(
    userId =>
      deleteEventUser(
        eventId,
        userId
      ).then(res => res.data),

    {
      onSuccess: async () => {
        await refetchUsers();
      },

      onError: error => {
        alert(
          'Error deleting user: ' +
            (
              error.response?.data?.message ||
              'Unknown error'
            )
        );
      },
    }
  );

  // =====================================================
  // COPY USERS
  // =====================================================

  const copyUsersMutation = useApiMutation(
    ({ targetEventId, sourceEventId }) =>
      copyUsersToEvent(
        targetEventId,
        sourceEventId
      ).then(res => res.data),

    {
      onSuccess: async () => {
        alert(
          'Users copied successfully'
        );

        setShowCopyModal(false);

        await refetchUsers();
      },

      onError: () => {
        alert(
          'Failed to copy users'
        );
      },
    }
  );

  // =====================================================
  // SOCKET
  // =====================================================

  useEffect(() => {
    if (!socket) return;

    const handleRefresh = () => {
      refetchUsers();
    };

    socket.on(
      'eventUserCreated',
      handleRefresh
    );

    socket.on(
      'eventUserUpdated',
      handleRefresh
    );

    socket.on(
      'eventUserDeleted',
      handleRefresh
    );

    socket.on(
      'eventUsersCopied',
      handleRefresh
    );

    socket.on(
      'customFieldsChanged',
      refetchCustomFields
    );

    return () => {
      socket.off(
        'eventUserCreated',
        handleRefresh
      );

      socket.off(
        'eventUserUpdated',
        handleRefresh
      );

      socket.off(
        'eventUserDeleted',
        handleRefresh
      );

      socket.off(
        'eventUsersCopied',
        handleRefresh
      );

      socket.off(
        'customFieldsChanged',
        refetchCustomFields
      );
    };
  }, [
    socket,
    refetchUsers,
    refetchCustomFields,
  ]);

  // =====================================================
  // QR MODAL
  // =====================================================

  const openQRModal = useCallback(
    user => {
      setSelectedUser(user);
      setShowQRModal(true);
    },
    []
  );

  const closeQRModal = useCallback(() => {
    setShowQRModal(false);
    setSelectedUser(null);
  }, []);

  // =====================================================
  // STOP QR SCANNER
  // =====================================================

  const stopScanner = useCallback(
    async () => {
      if (!scannerRef.current) {
        return;
      }

      try {
        await scannerRef.current.stop();
      } catch (error) {
        console.log(
          'Scanner stop:',
          error
        );
      }

      try {
        await scannerRef.current.clear();
      } catch (error) {
        console.log(
          'Scanner clear:',
          error
        );
      }

      scannerRef.current = null;
    },
    []
  );

  // =====================================================
  // CLOSE ADD/EDIT MODAL
  // =====================================================

  const handleCloseModal = useCallback(
    async () => {
      await stopScanner();

      setShowModal(false);
      setEditingUser(null);

      setShowScanner(false);
      setScannerError('');

      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'user',
        status: 'active',
        customId: '',
        userId: '',
      });

      setCustomData({});
    },
    [stopScanner]
  );

  // =====================================================
  // QR SCAN RESULT
  // =====================================================

  const handleScanResult = useCallback(
    async decodedText => {
      try {
        await stopScanner();

        setShowScanner(false);
        setScannerError('');

        let qrData;

        // Parse QR JSON
        try {
          qrData = JSON.parse(
            decodedText
          );
        } catch {
          // Fallback if QR only contains ID
          qrData = {
            userId: decodedText,
          };
        }

        // Make sure it is attendee QR
        if (
          qrData.type &&
          qrData.type !== 'attendee'
        ) {
          setScannerError(
            'This QR code is not an attendee QR.'
          );

          return;
        }

        const userId =
          qrData.userId;

        if (!userId) {
          setScannerError(
            'Invalid attendee QR code.'
          );

          return;
        }

        // =================================================
        // FIND EXISTING ATTENDEE
        // =================================================

        const response =
          await lookupAttendeeForEvent(
            eventId,
            userId
          );

        const attendee =
          response.data;

        // =================================================
        // FILL FORM
        // =================================================

        setFormData(prev => ({
          ...prev,

          // Existing attendee's permanent User ID
          userId: attendee.userId,

          // Name from registration
          // Admin can edit this
          name:
            attendee.name || '',

          // Email from registration
          // Email will be locked
          email:
            attendee.email || '',

          // Admin enters event-specific ID
          customId: '',

          // Existing attendee does not need password
          password: '',

          status:
            attendee.status ||
            'active',

          role: 'user',
        }));

        setShowModal(true);

      } catch (error) {
        console.error(
          'QR lookup error:',
          error
        );

        setScannerError(
          error.response?.data?.message ||
            'Unable to find attendee.'
        );

        setShowScanner(true);
      }
    },
    [
      eventId,
      stopScanner,
    ]
  );

  // =====================================================
  // START QR SCANNER
  // =====================================================

  const startScanner = useCallback(
    async () => {
      try {
        setScannerError('');
        setShowScanner(true);

        // Wait for DOM
        await new Promise(resolve =>
          setTimeout(resolve, 200)
        );

        if (scannerRef.current) {
          return;
        }

        const scanner =
          new Html5Qrcode(
            'attendee-qr-reader'
          );

        scannerRef.current =
          scanner;

        await scanner.start(
          {
            facingMode:
              'environment',
          },

          {
            fps: 10,

            qrbox: {
              width: 250,
              height: 250,
            },

            aspectRatio: 1,
          },

          decodedText => {
            handleScanResult(
              decodedText
            );
          },

          () => {
            // QR not found.
            // Ignore continuous scanner messages.
          }
        );

      } catch (error) {
        console.error(
          'Unable to start camera:',
          error
        );

        scannerRef.current =
          null;

        setScannerError(
          'Unable to access camera. Please allow camera permission and try again.'
        );
      }
    },
    [handleScanResult]
  );

  // =====================================================
  // COPY USERS
  // =====================================================

  const handleCopyUsers =
    useCallback(() => {
      if (!selectedSourceEventId) {
        alert(
          'Please select an event to copy from'
        );

        return;
      }

      copyUsersMutation.mutate({
        targetEventId: eventId,
        sourceEventId:
          selectedSourceEventId,
      });
    }, [
      selectedSourceEventId,
      eventId,
      copyUsersMutation,
    ]);

  // =====================================================
  // SUBMIT FORM
  // =====================================================

  const handleSubmit =
    useCallback(
      e => {
        e.preventDefault();

        const payload = {
          ...formData,
          customData,
        };

        // Existing attendee scanned through QR
        if (formData.userId) {
          // Never send password
          delete payload.password;
        }

        if (editingUser) {
          updateUserMutation.mutate({
            userId:
              editingUser._id,

            data: payload,
          });
        } else {
          addUserMutation.mutate(
            payload
          );
        }
      },
      [
        formData,
        customData,
        editingUser,
        addUserMutation,
        updateUserMutation,
      ]
    );

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete =
    useCallback(
      id => {
        if (
          window.confirm(
            'Are you sure you want to delete this user?'
          )
        ) {
          deleteUserMutation.mutate(
            id
          );
        }
      },
      [deleteUserMutation]
    );

  // =====================================================
  // EDIT
  // =====================================================

  const handleEdit =
    useCallback(user => {
      setEditingUser(user);

      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: 'user',
        status:
          user.status || 'active',
        customId:
          user.customId || '',
        userId: '',
      });

      setCustomData(
        user.customData || {}
      );

      setShowModal(true);
    }, []);

  // =====================================================
  // CUSTOM FIELD RENDER
  // =====================================================

  const renderDynamicField =
    useCallback(
      field => {
        const value =
          customData[field.name] ||
          '';

        const onChange = e => {
          setCustomData(prev => ({
            ...prev,

            [field.name]:
              e.target.value,
          }));
        };

        const required =
          field.required;

        switch (field.type) {
          case 'text':
          case 'email':
            return (
              <input
                type={field.type}
                value={value}
                onChange={onChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                required={required}
              />
            );

          case 'number':
            return (
              <input
                type="number"
                value={value}
                onChange={onChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                required={required}
              />
            );

          case 'date':
            return (
              <input
                type="date"
                value={value}
                onChange={onChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                required={required}
              />
            );

          case 'textarea':
            return (
              <textarea
                value={value}
                onChange={onChange}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                required={required}
              />
            );

          case 'select':
            return (
              <select
                value={value}
                onChange={onChange}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                required={required}
              >
                <option value="">
                  Select...
                </option>

                {field.options?.map(
                  option => (
                    <option
                      key={option}
                      value={option}
                    >
                      {option}
                    </option>
                  )
                )}
              </select>
            );

          default:
            return null;
        }
      },
      [customData]
    );

  // =====================================================
  // LOADING
  // =====================================================

  if (usersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">

          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />

          <p className="text-gray-500 text-sm">
            Loading users...
          </p>

        </div>
      </div>
    );
  }

  const isSubmitting =
    addUserMutation.isPending ||
    updateUserMutation.isPending;

  // =====================================================
  // UI
  // =====================================================

  return (
    <div className="space-y-6">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

        <div>

          <h1 className="text-2xl font-bold text-gray-900">
            Users Management
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Manage attendees for this event
          </p>

        </div>

        <div className="flex flex-wrap gap-2">

          {/* COPY */}

          <button
            onClick={() =>
              setShowCopyModal(true)
            }
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
          >
            <ClipboardIcon className="w-4 h-4" />

            Copy from Event
          </button>

          {/* ADD */}

          <button
            onClick={() => {

              setEditingUser(null);

              setFormData({
                name: '',
                email: '',
                password: '',
                role: 'user',
                status: 'active',
                customId: '',
                userId: '',
              });

              setCustomData({});

              setShowModal(true);

            }}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-4 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-[#0f5a9e] transition-all disabled:opacity-50"
          >
            <PlusIcon className="w-4 h-4" />

            Add User
          </button>

        </div>

      </div>

      {/* =================================================
          USERS TABLE
      ================================================= */}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">

          <div className="flex items-center gap-2">

            <UserGroupIcon className="w-5 h-5 text-gray-400" />

            <h2 className="text-base font-medium text-gray-900">
              Registered Users
            </h2>

            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {users.length} users
            </span>

          </div>

        </div>

        <div className="overflow-x-auto">

          <table className="min-w-full">

            <thead className="bg-gray-50/50">

              <tr>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  User ID
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Email
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>

                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Created
                </th>

                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody className="divide-y divide-gray-100">

              {users.length === 0 ? (

                <tr>

                  <td
                    colSpan="6"
                    className="text-center py-12"
                  >

                    <p className="text-gray-500">
                      No users found
                    </p>

                    <p className="text-sm text-gray-400 mt-1">
                      Click "Add User" to get started
                    </p>

                  </td>

                </tr>

              ) : (

                users.map(user => (

                  <tr
                    key={user._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {user.customId || '—'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.name}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user.email}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">

                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'active'
                            ? 'bg-[#1672b9]/10 text-[#1672b9]'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {user.status}
                      </span>

                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.createdAt
                        ? new Date(
                            user.createdAt
                          ).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            }
                          )
                        : '—'}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">

                      <button
                        onClick={() =>
                          handleEdit(user)
                        }
                        disabled={
                          isSubmitting
                        }
                        className="text-gray-400 hover:text-[#1672b9] transition-colors mr-3 disabled:opacity-50"
                        title="Edit"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>

                      <button
                        onClick={() =>
                          handleDelete(
                            user._id
                          )
                        }
                        disabled={
                          deleteUserMutation.isPending
                        }
                        className="text-gray-400 hover:text-gray-600 transition-colors mr-3 disabled:opacity-50"
                        title="Delete"
                      >
                        {deleteUserMutation.isPending &&
                        deleteUserMutation.variables ===
                          user._id ? (
                          <span className="inline-block w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <TrashIcon className="w-4 h-4" />
                        )}
                      </button>

                      <button
                        onClick={() =>
                          openQRModal(user)
                        }
                        className="text-gray-400 hover:text-[#1672b9] transition-colors"
                        title="Show QR"
                      >
                        <QrCodeIcon className="w-4 h-4" />
                      </button>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

          </table>

        </div>

      </div>

      {/* =================================================
          ADD / EDIT MODAL
      ================================================= */}

      {showModal && (

        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">

          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">

            {/* HEADER */}

            <div className="flex items-center justify-between p-5 border-b border-gray-100">

              <h3 className="text-lg font-medium text-gray-900">
                {editingUser
                  ? 'Edit User'
                  : 'Add New User'}
              </h3>

              <button
                onClick={
                  handleCloseModal
                }
                disabled={isSubmitting}
                className="text-gray-400 hover:text-[#1672b9]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

            </div>

            {/* FORM */}

            <form
              onSubmit={handleSubmit}
              className="p-5 space-y-4"
            >

              {/* =================================================
                  SCAN BUTTON
              ================================================= */}

              {!editingUser &&
                !formData.userId && (

                  <div>

                    <button
                      type="button"
                      onClick={
                        startScanner
                      }
                      disabled={
                        isSubmitting
                      }
                      className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition"
                    >
                      <CameraIcon className="w-5 h-5" />

                      Scan Attendee QR
                    </button>

                    <p className="text-xs text-gray-400 text-center mt-2">
                      Scan the QR generated from the attendee profile.
                    </p>

                  </div>

                )}

              {/* =================================================
                  CAMERA
              ================================================= */}

              {showScanner && (

                <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">

                  <div className="flex items-center justify-between mb-3">

                    <p className="text-sm font-medium text-gray-900">
                      Scan Attendee QR
                    </p>

                    <button
                      type="button"
                      onClick={async () => {
                        await stopScanner();

                        setShowScanner(
                          false
                        );

                        setScannerError(
                          ''
                        );
                      }}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>

                  </div>

                  <div
                    id="attendee-qr-reader"
                    className="w-full overflow-hidden rounded-lg"
                  />

                  <p className="text-xs text-gray-500 text-center mt-3">
                    Point the camera at the attendee's QR code.
                  </p>

                  {scannerError && (

                    <div className="mt-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                      {scannerError}
                    </div>

                  )}

                </div>

              )}

              {/* =================================================
                  ATTENDEE FOUND
              ================================================= */}

              {formData.userId &&
                !editingUser && (

                  <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">

                    <p className="text-sm font-medium text-green-800">
                      Attendee identified
                    </p>

                    <p className="text-xs text-green-700 mt-1">
                      Existing registered attendee found.
                    </p>

                  </div>

                )}

              {/* =================================================
                  NAME
              ================================================= */}

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>

                <input
                  type="text"
                  value={
                    formData.name
                  }
                  onChange={e =>
                    setFormData({
                      ...formData,
                      name:
                        e.target.value,
                    })
                  }
                  placeholder="Enter full name"
                  required
                  disabled={
                    isSubmitting
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                />

                {formData.userId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Name was filled from the attendee account and can be edited.
                  </p>
                )}

              </div>

              {/* =================================================
                  EMAIL
              ================================================= */}

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>

                <div className="relative">

                  <input
                    type="email"
                    value={
                      formData.email
                    }
                    onChange={e =>
                      setFormData({
                        ...formData,
                        email:
                          e.target.value,
                      })
                    }
                    placeholder="Email address"
                    required
                    disabled={
                      isSubmitting ||
                      !!formData.userId
                    }
                    className={`w-full border border-gray-200 rounded-lg px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9] ${
                      formData.userId
                        ? 'bg-gray-50 text-gray-500 cursor-not-allowed'
                        : ''
                    }`}
                  />

                  {formData.userId && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      
                    </span>
                  )}

                </div>

                {formData.userId && (
                  <p className="text-xs text-gray-400 mt-1">
                    Email comes from the registered attendee account and cannot be changed.
                  </p>
                )}

              </div>

              {/* =================================================
                  EVENT USER ID
              ================================================= */}

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  User ID (Unique)
                </label>

                <input
                  type="text"
                  value={
                    formData.customId
                  }
                  onChange={e =>
                    setFormData({
                      ...formData,
                      customId:
                        e.target.value,
                    })
                  }
                  placeholder="Enter event-specific ID"
                  required
                  disabled={
                    isSubmitting
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                />

                <p className="text-xs text-gray-400 mt-1">
                  Enter the unique code assigned to this attendee for this event.
                </p>

              </div>

              {/* =================================================
                  PASSWORD
              ================================================= */}

              {!editingUser && (

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-1">

                    Password

                    <span className="text-gray-400 font-normal">
                      {' '}
                      (optional)
                    </span>

                  </label>

                  <input
                    type="password"
                    value={
                      formData.password
                    }
                    onChange={e =>
                      setFormData({
                        ...formData,
                        password:
                          e.target.value,
                      })
                    }
                    placeholder={
                      formData.userId
                        ? 'Not required for QR attendee'
                        : 'Optional'
                    }
                    disabled={
                      isSubmitting ||
                      !!formData.userId
                    }
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  />

                  {formData.userId && (
                    <p className="text-xs text-green-600 mt-1">
                      Existing attendee account found. Password is not required.
                    </p>
                  )}

                </div>

              )}

              {/* =================================================
                  STATUS
              ================================================= */}

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>

                <select
                  value={
                    formData.status
                  }
                  onChange={e =>
                    setFormData({
                      ...formData,
                      status:
                        e.target.value,
                    })
                  }
                  disabled={
                    isSubmitting
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="active">
                    Active
                  </option>

                  <option value="inactive">
                    Inactive
                  </option>
                </select>

              </div>

              {/* =================================================
                  CUSTOM FIELDS
              ================================================= */}

              {customFields.map(
                field => (

                  <div
                    key={
                      field._id
                    }
                  >

                    <label className="block text-sm font-medium text-gray-700 mb-1">

                      {field.label}

                      {field.required &&
                        ' *'}

                    </label>

                    {renderDynamicField(
                      field
                    )}

                  </div>

                )
              )}

              {/* =================================================
                  FORM BUTTONS
              ================================================= */}

              <div className="flex justify-end gap-3 pt-2">

                <button
                  type="button"
                  onClick={
                    handleCloseModal
                  }
                  disabled={
                    isSubmitting
                  }
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.name.trim() ||
                    !formData.email.trim() ||
                    !formData.customId.trim()
                  }
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50"
                >
                  {isSubmitting
                    ? 'Saving...'
                    : editingUser
                    ? 'Update User'
                    : 'Create User'}
                </button>

              </div>

            </form>

          </div>

        </div>

      )}

      {/* =================================================
          COPY MODAL
      ================================================= */}

      {showCopyModal && (

        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">

          <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto">

            <div className="flex items-center justify-between p-5 border-b border-gray-100">

              <h3 className="text-lg font-medium text-gray-900">
                Copy Users from Another Event
              </h3>

              <button
                onClick={() =>
                  setShowCopyModal(
                    false
                  )
                }
                className="text-gray-400 hover:text-[#1672b9]"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

            </div>

            <div className="p-5 space-y-4">

              <div>

                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Source Event
                </label>

                <select
                  value={
                    selectedSourceEventId
                  }
                  onChange={e =>
                    setSelectedSourceEventId(
                      e.target.value
                    )
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                >

                  <option value="">
                    Choose an event
                  </option>

                  {copyableEvents.map(
                    event => (

                      <option
                        key={
                          event._id
                        }
                        value={
                          event._id
                        }
                      >
                        {event.name}
                      </option>

                    )
                  )}

                </select>

              </div>

              <div className="flex justify-end gap-3">

                <button
                  onClick={() =>
                    setShowCopyModal(
                      false
                    )
                  }
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
                >
                  Cancel
                </button>

                <button
                  onClick={
                    handleCopyUsers
                  }
                  disabled={
                    copyUsersMutation.isPending ||
                    !selectedSourceEventId
                  }
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm disabled:opacity-50"
                >
                  {copyUsersMutation.isPending
                    ? 'Copying...'
                    : 'Copy Users'}
                </button>

              </div>

            </div>

          </div>

        </div>

      )}

      {/* =================================================
          EVENT USER QR
      ================================================= */}

      {showQRModal &&
        selectedUser && (

          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">

            <div className="relative bg-white rounded-xl shadow-xl max-w-sm w-full mx-auto">

              <div className="flex items-center justify-between p-5 border-b border-gray-100">

                <h3 className="text-lg font-medium text-gray-900">
                  QR Code for{' '}
                  {
                    selectedUser.name
                  }
                </h3>

                <button
                  onClick={
                    closeQRModal
                  }
                  className="text-gray-400 hover:text-[#1672b9]"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>

              </div>

              <div className="p-6 flex flex-col items-center">

                <div className="bg-white p-4 rounded-lg border border-gray-200">

                  <QRCodeCanvas
                    value={
                      selectedUser.customId
                    }
                    size={200}
                  />

                </div>

                <p className="text-center mt-4 text-sm text-gray-600">

                  User ID:{' '}

                  <span className="font-mono font-semibold text-gray-900">
                    {
                      selectedUser.customId
                    }
                  </span>

                </p>

              </div>

              <div className="p-5 border-t border-gray-100 flex justify-end">

                <button
                  onClick={
                    closeQRModal
                  }
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm"
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        )}

    </div>
  );
};

export default Users;